/**
 * Ticket age ranges (booking seats, Phase 5; plans/booking-seats.md).
 *
 * A ticket type can carry an optional `minAge` / `maxAge` (whole years,
 * inclusive). An attendee whose date of birth puts them outside the range on
 * the day of the session gets a SOFT warning: shown to the shopper at
 * checkout and to staff on the booking, never a block. Shared by the API, the
 * dashboard and the checkout so all three say the same thing.
 */

export type TicketAgeRange = { minAge?: number | null; maxAge?: number | null };

export type TicketAgeWarning = {
  kind: "too-young" | "too-old";
  age: number;
  minAge: number | null;
  maxAge: number | null;
};

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A YYYY-MM-DD string's parts, or null when it isn't a real date. */
function dateParts(value: string): { y: number; m: number; d: number } | null {
  const match = DATE_ONLY.exec(value.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const check = new Date(Date.UTC(y, m - 1, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) return null;
  return { y, m, d };
}

/**
 * Whole years old on `on`. `dateOfBirth` is the stored YYYY-MM-DD; `on` is a
 * YYYY-MM-DD day or a Date (its UTC calendar day). Null for a missing or
 * invalid birth date, a birth after `on`, or an implausible age (130+).
 */
export function ageOnDate(dateOfBirth: string | null | undefined, on: Date | string): number | null {
  if (typeof dateOfBirth !== "string") return null;
  const dob = dateParts(dateOfBirth);
  if (!dob) return null;
  let day: { y: number; m: number; d: number } | null;
  if (typeof on === "string") {
    day = dateParts(on.slice(0, 10));
  } else {
    day = Number.isNaN(on.getTime())
      ? null
      : { y: on.getUTCFullYear(), m: on.getUTCMonth() + 1, d: on.getUTCDate() };
  }
  if (!day) return null;
  let age = day.y - dob.y;
  if (day.m < dob.m || (day.m === dob.m && day.d < dob.d)) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

/** A stored range bound as a whole number of years, or null when unset. */
export function ticketAgeBound(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n < 130 ? n : null;
}

/** Whether the ticket has any age range at all. */
export function hasTicketAgeRange(range: TicketAgeRange | null | undefined): boolean {
  return ticketAgeBound(range?.minAge) !== null || ticketAgeBound(range?.maxAge) !== null;
}

/**
 * The warning for one attendee on one ticket, or null when the ticket has no
 * range, the age is unknown, or it fits.
 */
export function ticketAgeWarning(
  range: TicketAgeRange | null | undefined,
  dateOfBirth: string | null | undefined,
  on: Date | string,
): TicketAgeWarning | null {
  const minAge = ticketAgeBound(range?.minAge);
  const maxAge = ticketAgeBound(range?.maxAge);
  if (minAge === null && maxAge === null) return null;
  const age = ageOnDate(dateOfBirth, on);
  if (age === null) return null;
  if (minAge !== null && age < minAge) return { kind: "too-young", age, minAge, maxAge };
  if (maxAge !== null && age > maxAge) return { kind: "too-old", age, minAge, maxAge };
  return null;
}

/** "Ages 5–11", "Ages 18+", "Up to age 4"; "" when the ticket has no range. */
export function ticketAgeRangeLabel(range: TicketAgeRange | null | undefined): string {
  const minAge = ticketAgeBound(range?.minAge);
  const maxAge = ticketAgeBound(range?.maxAge);
  if (minAge !== null && maxAge !== null) return minAge === maxAge ? `Age ${minAge}` : `Ages ${minAge}–${maxAge}`;
  if (minAge !== null) return `Ages ${minAge}+`;
  if (maxAge !== null) return `Up to age ${maxAge}`;
  return "";
}

/** "Maya is 4 on the day; this ticket is for ages 5–11." */
export function ticketAgeWarningMessage(warning: TicketAgeWarning, who?: string | null): string {
  const subject = who?.trim() ? `${who.trim()} is` : "This person is";
  const label = ticketAgeRangeLabel(warning).replace(/^A/, "a").replace(/^Up/, "up");
  return `${subject} ${warning.age} on the day; this ticket is for ${label}.`;
}
